import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { PageHeader } from './_shared';
import {
  LOW_STOCK,
  MARGIN_ROWS,
  PERIOD_LABEL,
  VALUATION,
  peso,
  pesoShort,
} from './sample-data';

export function CostingPage() {
  const totals = MARGIN_ROWS.reduce(
    (acc, r) => ({
      qty: acc.qty + r.qty,
      revenue: acc.revenue + r.revenue,
      cost: acc.cost + r.cost,
    }),
    { qty: 0, revenue: 0, cost: 0 },
  );
  const totalMargin = totals.revenue - totals.cost;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Costing & Margins"
        subtitle={`${PERIOD_LABEL} · cost captured at the point of sale, so margin is locked to what you actually paid`}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <p className="text-[12px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Inventory at cost
            </p>
            <p className="mt-1 text-[1.75rem] font-semibold leading-8 tabular-nums">
              {pesoShort(VALUATION.atCost)}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {VALUATION.skus} SKUs · {VALUATION.units.toLocaleString('en-PH')} units on hand
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-[12px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Margin — top products
            </p>
            <p className="mt-1 text-[1.75rem] font-semibold leading-8 tabular-nums text-credit">
              {pesoShort(totalMargin)}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {((totalMargin / totals.revenue) * 100).toFixed(1)}% on{' '}
              {pesoShort(totals.revenue)} revenue
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-[12px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Lines without a cost
            </p>
            <p className="mt-1 text-[1.75rem] font-semibold leading-8 tabular-nums text-credit">
              {VALUATION.uncostedLines}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Every sold line carries a cost, so nothing is missing from margin.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Margin by product{' '}
            <span className="text-sm font-normal text-muted-foreground">
              (top {MARGIN_ROWS.length} of {VALUATION.skus})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">SKU</th>
                  <th className="px-4 py-2">Product</th>
                  <th className="px-4 py-2 text-right">Qty sold</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2 text-right">Cost</th>
                  <th className="px-4 py-2 text-right">Margin</th>
                  <th className="px-4 py-2 text-right">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {MARGIN_ROWS.map((r) => {
                  const margin = r.revenue - r.cost;
                  return (
                    <tr key={r.sku} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2 font-mono text-xs">{r.sku}</td>
                      <td className="px-4 py-2">{r.name}</td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {r.qty.toLocaleString('en-PH')}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {peso(r.revenue)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {peso(r.cost)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums text-credit">
                        {peso(margin)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {((margin / r.revenue) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 font-semibold">
                  <td className="px-4 py-2" />
                  <td className="px-4 py-2">Totals</td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">
                    {totals.qty.toLocaleString('en-PH')}
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">
                    {peso(totals.revenue)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">
                    {peso(totals.cost)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums text-credit">
                    {peso(totalMargin)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">
                    {((totalMargin / totals.revenue) * 100).toFixed(1)}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Below reorder point</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-2">
            {LOW_STOCK.map((r) => (
              <Badge key={`${r.sku}-${r.branch}`} variant="warning" className="normal-case">
                {r.name} · {r.branch} — {r.onHand} left (reorder at {r.reorderPoint})
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
